/**
 * Craftpedia Hero Predictive Search
 * Handles real-time search suggestions for blog articles as the user types.
 *
 * Implements AGENTS-1.md CRO JavaScript guidelines:
 * - Scoped IIFE, zero global variables
 * - data-* attribute DOM queries
 * - Pure data sanitisation & defensive execution
 * - AbortController for race condition prevention
 *
 * Positioning strategy:
 * .craftpedia-hero has overflow:hidden which clips any absolutely-positioned
 * child that overflows. We use a "portal" pattern — the dropdown is appended
 * to <body> and repositioned with getBoundingClientRect() on every open so
 * it always floats below the search bar regardless of containing overflow.
 */

(function () {
  'use strict';

  var DEBOUNCE_DELAY_MS = 250;
  var MIN_SEARCH_LENGTH = 2;
  var PREDICTIVE_ENDPOINT = '/search/suggest.json';

  /* ------------------------------------------------------------------ */
  /* Utilities                                                            */
  /* ------------------------------------------------------------------ */

  function sanitizeText(rawString) {
    if (!rawString || typeof rawString !== 'string') {
      return '';
    }
    var element = document.createElement('div');
    element.textContent = rawString;
    return element.innerHTML;
  }

  function getPrimaryTag(tagList) {
    if (!Array.isArray(tagList) || tagList.length === 0) {
      return 'Blog Article';
    }
    for (var i = 0; i < tagList.length; i++) {
      var tag = tagList[i];
      if (typeof tag === 'string' && tag.indexOf('Primary Craft - ') === 0) {
        return tag.replace('Primary Craft - ', '').trim();
      }
    }
    for (var j = 0; j < tagList.length; j++) {
      var pillarTag = tagList[j];
      if (typeof pillarTag === 'string' && pillarTag.indexOf('Pillar - ') === 0) {
        return pillarTag.replace('Pillar - ', '').trim();
      }
    }
    return 'Blog Article';
  }

  /* ------------------------------------------------------------------ */
  /* Portal dropdown factory                                              */
  /* Creates and appends the dropdown to <body> so it escapes any parent */
  /* overflow:hidden context.                                             */
  /* ------------------------------------------------------------------ */

  function createPortalDropdown() {
    var dropdown = document.createElement('div');
    dropdown.className = 'craftpedia-search-dropdown craftpedia-search-dropdown--portal';
    dropdown.setAttribute('role', 'region');
    dropdown.setAttribute('aria-label', 'Search suggestions');
    dropdown.setAttribute('aria-hidden', 'true');
    dropdown.setAttribute('data-craftpedia-search-dropdown', '');

    var results = document.createElement('div');
    results.setAttribute('data-craftpedia-search-results', '');
    dropdown.appendChild(results);

    document.body.appendChild(dropdown);
    return { dropdown: dropdown, results: results };
  }

  /* ------------------------------------------------------------------
     Positioning: position:fixed + getBoundingClientRect()
     offsetParent walking is unreliable inside flex containers.
     getBoundingClientRect() is always viewport-accurate.
     Dropdown is clamped below the sticky site header and
     auto-closes if the form scrolls behind it.
     ------------------------------------------------------------------ */

  function getStickyHeaderBottom() {
    try {
      var header =
        document.querySelector('sticky-header') ||
        document.querySelector('.header-wrapper') ||
        document.querySelector('header.header');
      return header ? header.getBoundingClientRect().bottom : 0;
    } catch (e) {
      return 0;
    }
  }

  function positionDropdown(dropdownElement, anchorElement, closeFn) {
    try {
      var rect = anchorElement.getBoundingClientRect();
      var headerBottom = getStickyHeaderBottom();

      /* Close if form has scrolled behind the header or off screen */
      if (rect.bottom <= headerBottom || rect.top >= window.innerHeight) {
        if (closeFn) { closeFn(); }
        return;
      }

      var top = Math.max(rect.bottom + 8, headerBottom + 8);

      dropdownElement.style.position = 'fixed';
      dropdownElement.style.top = top + 'px';
      dropdownElement.style.left = rect.left + 'px';
      dropdownElement.style.width = rect.width + 'px';
      dropdownElement.style.zIndex = '99999';
    } catch (error) {
      // Defensive: fail safely
    }
  }

  /* ------------------------------------------------------------------ */
  /* Search controller                                                    */
  /* ------------------------------------------------------------------ */

  function createSearchController(formElement) {
    if (!formElement) {
      return null;
    }

    var searchInput = formElement.querySelector('[data-craftpedia-search-input]');
    if (!searchInput) {
      return null;
    }

    /* Create the portal dropdown (appended to <body>) */
    var portal = createPortalDropdown();
    var dropdownElement = portal.dropdown;
    var resultsElement = portal.results;

    var debounceTimer = null;
    var abortController = null;
    var currentQuery = '';
    var hasResults = false;

    /* ---- Dropdown open / close ---- */

    function openDropdown() {
      try {
        positionDropdown(dropdownElement, formElement, closeDropdown);
        dropdownElement.classList.add('is-open');
        dropdownElement.setAttribute('aria-hidden', 'false');
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function closeDropdown() {
      try {
        dropdownElement.classList.remove('is-open');
        dropdownElement.setAttribute('aria-hidden', 'true');
      } catch (error) {
        // Defensive: fail safely
      }
    }

    /* ---- Render states ---- */

    function renderLoadingState() {
      try {
        resultsElement.innerHTML =
          '<div class="craftpedia-search-dropdown__loading">' +
          '<div class="craftpedia-search-dropdown__spinner"></div>' +
          '<span>Searching articles...</span>' +
          '</div>';
        openDropdown();
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function renderEmptyState(query) {
      try {
        var cleanQuery = sanitizeText(query);
        resultsElement.innerHTML =
          '<div class="craftpedia-search-dropdown__message">' +
          'No articles found for "' + cleanQuery + '"' +
          '</div>';
        hasResults = false;
        openDropdown();
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function renderSuggestions(articles, query) {
      try {
        if (!Array.isArray(articles) || articles.length === 0) {
          renderEmptyState(query);
          return;
        }

        var listHtml = '<ul class="craftpedia-search-dropdown__list" role="list">';
        var maxCount = Math.min(articles.length, 6);

        for (var index = 0; index < maxCount; index++) {
          var article = articles[index];
          if (!article || !article.url) {
            continue;
          }

          var title = sanitizeText(article.title || 'Untitled');
          var url = article.url;
          var imageUrl = article.image || (article.featured_image ? article.featured_image.url : '');
          var tagLabel = sanitizeText(getPrimaryTag(article.tags));

          var imageMarkup = imageUrl
            ? '<img class="craftpedia-search-dropdown__image" src="' + sanitizeText(imageUrl) + '" alt="' + title + '" width="48" height="48" loading="lazy">'
            : '<div class="craftpedia-search-dropdown__placeholder-icon" aria-hidden="true">📖</div>';

          listHtml +=
            '<li>' +
            '<a class="craftpedia-search-dropdown__item" href="' + sanitizeText(url) + '" data-craftpedia-search-item>' +
            '<div class="craftpedia-search-dropdown__image-wrap">' + imageMarkup + '</div>' +
            '<div class="craftpedia-search-dropdown__info">' +
            '<span class="craftpedia-search-dropdown__tag">' + tagLabel + '</span>' +
            '<h4 class="craftpedia-search-dropdown__title">' + title + '</h4>' +
            '</div>' +
            '</a>' +
            '</li>';
        }

        listHtml += '</ul>';

        resultsElement.innerHTML = listHtml;
        hasResults = true;
        openDropdown();
      } catch (error) {
        // Defensive: fail safely
      }
    }

    /* ---- Fetch ---- */

    function fetchSuggestions(query) {
      try {
        if (abortController) {
          abortController.abort();
        }

        if (!query || query.length < MIN_SEARCH_LENGTH) {
          closeDropdown();
          resultsElement.innerHTML = '';
          hasResults = false;
          return;
        }

        abortController = new AbortController();
        renderLoadingState();

        var requestUrl =
          PREDICTIVE_ENDPOINT +
          '?q=' + encodeURIComponent(query) +
          '&resources[type]=article' +
          '&resources[options][fields]=title,body,tag';

        fetch(requestUrl, {
          signal: abortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Network error');
            }
            return response.json();
          })
          .then(function (data) {
            var articles = [];
            if (
              data &&
              data.resources &&
              data.resources.results &&
              Array.isArray(data.resources.results.articles)
            ) {
              articles = data.resources.results.articles;
            }
            renderSuggestions(articles, query);
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') {
              return;
            }
            renderEmptyState(query);
          });
      } catch (error) {
        // Defensive: fail safely
      }
    }

    /* ---- Event handlers ---- */

    function handleInput(event) {
      try {
        var query = (event.target.value || '').trim();
        currentQuery = query;

        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }

        if (query.length < MIN_SEARCH_LENGTH) {
          closeDropdown();
          return;
        }

        debounceTimer = setTimeout(function () {
          fetchSuggestions(query);
        }, DEBOUNCE_DELAY_MS);
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function handleFocus() {
      try {
        if (currentQuery.length >= MIN_SEARCH_LENGTH && hasResults) {
          openDropdown();
        }
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function handleClickOutside(event) {
      try {
        if (!formElement.contains(event.target) && !dropdownElement.contains(event.target)) {
          closeDropdown();
        }
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function handleKeydown(event) {
      try {
        if (event.key === 'Escape') {
          closeDropdown();
          searchInput.focus();
        }
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function handleScroll() {
      try {
        if (dropdownElement.classList.contains('is-open')) {
          /* Reposition on scroll; auto-closes if form goes behind header */
          positionDropdown(dropdownElement, formElement, closeDropdown);
        }
      } catch (error) {
        // Defensive: fail safely
      }
    }

    function handleResize() {
      try {
        if (dropdownElement.classList.contains('is-open')) {
          positionDropdown(dropdownElement, formElement, closeDropdown);
        }
      } catch (error) {
        // Defensive: fail safely
      }
    }

    searchInput.addEventListener('input', handleInput);
    searchInput.addEventListener('focus', handleFocus);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return {
      destroy: function () {
        try {
          searchInput.removeEventListener('input', handleInput);
          searchInput.removeEventListener('focus', handleFocus);
          document.removeEventListener('click', handleClickOutside);
          document.removeEventListener('keydown', handleKeydown);
          window.removeEventListener('scroll', handleScroll);
          window.removeEventListener('resize', handleResize);
          if (dropdownElement && dropdownElement.parentNode) {
            dropdownElement.parentNode.removeChild(dropdownElement);
          }
        } catch (error) {
          // Defensive: fail safely
        }
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function initCraftpediaSearch() {
    try {
      var forms = document.querySelectorAll('[data-craftpedia-search-form]');
      for (var index = 0; index < forms.length; index++) {
        createSearchController(forms[index]);
      }
    } catch (error) {
      // Defensive: fail safely
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCraftpediaSearch);
  } else {
    initCraftpediaSearch();
  }
})();
